import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { promptExamples } from '../data/examples';

const { FiCopy, FiCheck, FiX, FiFileText } = FiIcons;

const ExampleCard = ({ example, onClick }) => {
  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(example)}
      className="clay-card p-6 cursor-pointer h-full flex flex-col items-center justify-center text-center min-h-[140px] hover:bg-gray-800/50 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mb-3">
        <SafeIcon icon={FiFileText} className="text-green-400 text-lg" />
      </div>
      <h3 className="text-white font-medium text-lg leading-tight">{example.title}</h3>
      <p className="text-gray-400 text-xs mt-2">Click to view prompt</p>
    </motion.div>
  );
};

const ExampleModal = ({ example, onClose }) => {
  const [copied, setCopied] = useState(false);
  const jsonString = JSON.stringify(example.content, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        onClick={(e) => e.stopPropagation()}
        className="clay-card w-full max-w-2xl max-h-[80vh] flex flex-col p-0 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
          <h3 className="text-xl font-bold text-white">{example.title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-full transition-colors">
            <SafeIcon icon={FiX} className="text-white text-xl" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto custom-scrollbar">
          <div className="clay-code-block p-4 rounded-lg">
            <pre className="text-green-400 text-xs sm:text-sm whitespace-pre-wrap font-mono">
              {jsonString}
            </pre>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-700 bg-gray-900/50">
          <button
            onClick={handleCopy}
            className="w-full clay-button btn-primary py-3 px-4 font-semibold flex items-center justify-center gap-2 highlight-hover"
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} />
            {copied ? 'Copied to Clipboard!' : 'Copy JSON Prompt'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ExamplesSection = () => {
  const [selectedExample, setSelectedExample] = useState(null);

  return (
    <div className="mt-16 mb-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Example Prompts</h2>
        <p className="text-gray-400">Explore professionally crafted JSON prompts for various scenarios</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {promptExamples.map((example, index) => (
          <ExampleCard 
            key={index} 
            example={example} 
            onClick={setSelectedExample} 
          />
        ))}
      </div>

      <AnimatePresence>
        {selectedExample && (
          <ExampleModal 
            example={selectedExample} 
            onClose={() => setSelectedExample(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExamplesSection;